import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

const formatPhoneNumber = (input?: string | null | undefined): string => {
  if (!input) {
    return "--";
  }

  const cleaned = input.replaceAll(/\D/gu, "");
  // eslint-disable-next-line regexp/no-useless-quantifier, regexp/no-empty-alternative
  const match = /^(1|)?(\d{3})(\d{3})(\d{4})$/u.exec(cleaned);
  if (!match) {
    return "--";
  }

  const intlCode = match[1] ? "+1 " : "";
  return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
};

const timeIsRecent = (
  input?: Date | number | string,
  withinSeconds: number = 300,
): boolean => {
  if (!input) {
    return false;
  }

  const date = input instanceof Date ? input : new Date(input);
  const now = new Date();
  return now.valueOf() - date.valueOf() < 1_000 * withinSeconds;
};

const phoneMatcher = /^\+[1-9]\d{1,14}$/u;

const emailMatcher =
  // eslint-disable-next-line unicorn/no-unsafe-regex
  /^[\w!#$%&'*+./=?^`{|}~-]+@[\dA-Za-z](?:[\dA-Za-z-]{0,61}[\dA-Za-z])?(?:\.[\dA-Za-z](?:[\dA-Za-z-]{0,61}[\dA-Za-z])?)*$/u;

const isEmailAddress = (input: string) => {
  if (input.length > 320) return false;
  return emailMatcher.test(input);
};

const isPhoneNumber = (input: string) => {
  if (input.length > 16) return false;
  return phoneMatcher.test(input);
};

const formatBytes = (bytes: number, decimals = 2) => {
  if (!Number(bytes)) return "0 Bytes";

  const kilo = 1_024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [
    "Bytes",
    "KiB",
    "MiB",
    "GiB",
    "TiB",
    "PiB",
    "EiB",
    "ZiB",
    "YiB",
  ];

  const index = Math.floor(Math.log(bytes) / Math.log(kilo));

  return `${Number.parseFloat((bytes / kilo ** index).toFixed(dm))} ${
    sizes[index]
  }`;
};

export function sleep(ms: number = 1000) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function capitalizeFirstLetter(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Generates page numbers for pagination with ellipsis
 * @param currentPage - Current page number (1-based)
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and ellipsis strings
 *
 * Examples:
 * - Small dataset (≤5 pages): [1, 2, 3, 4, 5]
 * - Near beginning: [1, 2, 3, 4, '...', 10]
 * - In middle: [1, '...', 4, 5, 6, '...', 10]
 * - Near end: [1, '...', 7, 8, 9, 10]
 */
export function getPageNumbers(currentPage: number, totalPages: number) {
  const maxVisiblePages = 5; // Maximum number of page buttons to show
  const rangeWithDots = [];

  if (totalPages <= maxVisiblePages) {
    // If total pages is 5 or less, show all pages
    for (let i = 1; i <= totalPages; i++) {
      rangeWithDots.push(i);
    }
  } else {
    // Always show first page
    rangeWithDots.push(1);

    if (currentPage <= 3) {
      // Near the beginning: [1] [2] [3] [4] ... [10]
      for (let i = 2; i <= 4; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      // Near the end: [1] ... [7] [8] [9] [10]
      rangeWithDots.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
    } else {
      // In the middle: [1] ... [4] [5] [6] ... [10]
      rangeWithDots.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    }
  }

  return rangeWithDots;
}

export {
  cn,
  formatBytes,
  formatPhoneNumber,
  isEmailAddress,
  isPhoneNumber,
  timeIsRecent,
};
